import React, { useState } from "react";
import { Divider, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { MdDelete } from "react-icons/md";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery, useMutation } from "@apollo/client";
import claims from "../../../gql/claims";
import ClaimBulkStatsFilter from "./ClaimBulkStatsFilter";
import ConformationModal from "../../../components/ConformationModal";
import toast from "react-hot-toast";
import { convertToISOFormat } from "../ClaimHistoryList";
import { useTranslation } from "react-i18next";
import CardLoading from "../../../common/CardLoading";
import AppButton from "../../../shared/AppButton";

export default function ClaimsSubmitStats() {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmModalBulkSubmission, setShowConfirmModalBulkSubmission] =
    useState(false);
  const [
    showConfirmModalStopBulkSubmission,
    setShowConfirmModalStopBulkSubmission,
  ] = useState(false);

  const currentDate = new Date();
  const currentYearMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth()).padStart(2, "0")}`;

  function getFirstAndLastDateOfMonth(yearMonth) {
    const [year, month] = yearMonth.split("-").map(Number);
    const firstDate = new Date(year, month - 1, 1);
    const lastDate = new Date(year, month, 0);
    const firstDateString = `${firstDate.getFullYear()}-${(firstDate.getMonth() + 1).toString().padStart(2, "0")}-${firstDate.getDate().toString().padStart(2, "0")}`;
    const lastDateString = `${lastDate.getFullYear()}-${(lastDate.getMonth() + 1).toString().padStart(2, "0")}-${lastDate.getDate().toString().padStart(2, "0")}`;
    return { firstDate: firstDateString, lastDate: lastDateString };
  }

  const initialDates = getFirstAndLastDateOfMonth(currentYearMonth);
  const [fromDate, setFromDate] = useState(initialDates.firstDate);
  const [toDate, setToDate] = useState(initialDates.lastDate);
  const [showFilter, setShowFilter] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(null);

  const {
    loading: dataLoading,
    error,
    data,
    refetch,
  } = useQuery(claims.CLAIMS_BULK_SUBMISSION_STATS, {
    variables: { input: { from_date: fromDate, to_date: toDate } },
  });

  const getLastNMonths = (n) => {
    const months = [];
    for (let i = 0; i < n; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      months.push(date);
    }
    return months;
  };

  const handleMonthClick = (date, index) => {
    setClickedIndex(index);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const { firstDate, lastDate } = getFirstAndLastDateOfMonth(yearMonth);
    setFromDate(firstDate);
    setToDate(lastDate);
    refetch({ input: { from_date: firstDate, to_date: lastDate } });
  };

  const monthButtons = getLastNMonths(13).map((date, index) => {
    const isActive = convertToISOFormat(date.toLocaleDateString()) === fromDate;
    return (
      <AppButton
        key={index}
        label={`${date.getFullYear()} ${t(date.toLocaleString("default", { month: "long" }))}`}
        variant={isActive ? "contained" : "outlined"}
        onClick={() => handleMonthClick(date, index)}
        className={`!text-[11px] !py-0.5 whitespace-nowrap mb-1 ${
          isActive
            ? "!bg-[#0070C2] !text-white"
            : "!border-primary !text-primary hover:!bg-primary/10"
        }`}
      />
    );
  });

  // ── DELETE ──
  const [bulkDelete, setBulkDelete] = useState({ payerId: null, month: null });
  const [delEntry, { loading: delLoading }] = useMutation(
    claims.BULK_DELETE_SUBMISSION,
    {
      update(proxy, result) {
        onSuccessfulRequestResponse(result.data);
      },
      onError(error) {
        onResponseError(error.graphQLErrors[0]);
      },
    },
  );
  const onSuccessfulRequestResponse = (data) => {
    setShowConfirmModal(false);
    if (data?.bulkDeleteReadyForSubmission) {
      refetch({ input: { from_date: fromDate, to_date: toDate } });
      setBulkDelete({ payerId: null, month: null });
      toast.success(data?.bulkDeleteReadyForSubmission?.message);
    }
  };
  const onResponseError = (error) => {
    setShowConfirmModal(false);
    setBulkDelete({ payerId: null, month: null });
    toast.error(`${error?.message}`);
  };
  const deleteBulkClaim = () => {
    if (bulkDelete?.month && bulkDelete?.payerId) {
      delEntry({
        variables: {
          input: { month: bulkDelete?.month, payer_id: bulkDelete?.payerId },
        },
      });
    } else {
      toast.error(t("Something went wrong in bulk delete!"));
    }
  };

  // ── SUBMIT ──
  const [bulkSubmit, setBulkSubmit] = useState({ payerId: null, month: null });
  const [submitEntry, { loading: submitLoading }] = useMutation(
    claims.BULK_CLAIMS_SUBMISSION,
    {
      update(proxy, result) {
        onSuccessfulRequestResponsee(result.data);
      },
      onError(error) {
        onResponseErrorr(error.graphQLErrors[0]);
      },
    },
  );
  const onSuccessfulRequestResponsee = (data) => {
    setShowConfirmModalBulkSubmission(false);
    if (data?.submitBatchClaims) {
      refetch({ input: { from_date: fromDate, to_date: toDate } });
      setBulkSubmit({ payerId: null, month: null });
      toast.success(data?.submitBatchClaims?.message);
    }
  };
  const onResponseErrorr = (error) => {
    setShowConfirmModalBulkSubmission(false);
    setBulkSubmit({ payerId: null, month: null });
    toast.error(`${error?.message}`);
  };
  const submitBulkClaim = () => {
    if (bulkSubmit?.month && bulkSubmit?.payerId) {
      submitEntry({
        variables: {
          input: { month: bulkSubmit?.month, payer_id: bulkSubmit?.payerId },
        },
      });
    } else {
      toast.error(t("Something went wrong in bulk submission!"));
    }
  };

  // ── STOP ──
  const [bulkSubmitStop, setBulkSubmitStop] = useState({
    payerId: null,
    month: null,
  });
  const [submitStopEntry, { loading: submitStopLoading }] = useMutation(
    claims.BULK_STOP_SUBMISSION,
    {
      update(proxy, result) {
        onSuccessfulRequestResponseee(result.data);
      },
      onError(error) {
        onResponseErrorrr(error.graphQLErrors[0]);
      },
    },
  );
  const onSuccessfulRequestResponseee = (data) => {
    setShowConfirmModalStopBulkSubmission(false);
    if (data?.stopBatchClaimsQueue) {
      refetch({ input: { from_date: fromDate, to_date: toDate } });
      setBulkSubmitStop({ payerId: null, month: null });
      toast.success(data?.stopBatchClaimsQueue?.message);
    }
  };
  const onResponseErrorrr = (error) => {
    setShowConfirmModalStopBulkSubmission(false);
    setBulkSubmitStop({ payerId: null, month: null });
    toast.error(`${error?.message}`);
  };
  const submitStopBulkClaim = () => {
    if (bulkSubmitStop?.month && bulkSubmitStop?.payerId) {
      submitStopEntry({
        variables: {
          input: {
            month: bulkSubmitStop?.month,
            payer_id: bulkSubmitStop?.payerId,
          },
        },
      });
    } else {
      toast.error(t("Something went wrong in stop bulk submission!"));
    }
  };

  const statFields = [
    { label: t("Total Claims"), field: "total_claims" },
    { label: t("Total Discount Amount"), field: "total_discount_amount" },
    { label: t("Total Net Amount"), field: "total_net_amount" },
    { label: t("Total Quantity"), field: "total_quantity" },
    { label: t("Total Tax Amount"), field: "total_tax_amount" },
    { label: t("Total Unit Price"), field: "total_unit_price" },
  ];

  return (
    <>
      <div className="pre_auth_container !p-0">
        {/* Month Filter Strip */}
        <div className="flex flex-wrap gap-1.5 mb-4">{monthButtons}</div>

        {/* Content */}
        {submitLoading || submitStopLoading || delLoading || dataLoading ? (
          <CardLoading />
        ) : (
          data?.claimSubmissionStats?.data?.map((stats) => {
            const monthDate = new Date(stats.month);
            const monthName = monthDate.toLocaleString("default", {
              month: "long",
            });
            const year = monthDate.getFullYear();

            return (
              <div key={stats.month} className="mt-4">
                {/* Month Header */}
                {/* <div className="flex items-center px-2 py-1.5 rounded-md mb-2 border-y-2 border-gray-100">
                  <span className="text-[11px] font-bold text-[#1e293b] uppercase tracking-wide">
                    {t(monthName)}, {year}
                  </span>
                </div> */}

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stats.claims.map((claim) => (
                    <Paper
                      key={claim.payer_id}
                      elevation={0}
                      className="!rounded-lg overflow-hidden"
                      sx={{ border: "1px solid #e2e8f0" }}
                    >
                      {/* Card Header */}
                      <div className="px-2 py-2 border-b border-gray-300 bg-gray-100">
                        <p className="text-[11px] font-bold text-slate-700 uppercase tracking-tight text-center truncate">
                          {claim?.payer_name}
                        </p>
                      </div>

                      {/* Card Stats */}
                      <div className="p-2 grid grid-cols-2 gap-y-1.5 gap-x-2 bg-white">
                        {statFields.map(({ label, field }) => (
                          <div
                            key={field}
                            className="flex flex-col bg-slate-50/50 p-1.5 rounded border border-slate-100"
                          >
                            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">
                              {label}
                            </span>
                            <span className="text-[11px] font-semibold text-[#0070C2] truncate">
                              {claim?.[field] || "0"}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Card Actions */}
                      <div className="flex gap-1 px-2 pb-2 border-t border-gray-100 pt-2 bg-gray-50/80">
                        <AppButton
                          label={t("Submit")}
                          fullWidth
                          size="small"
                          startIcon={<AddIcon className="!text-[12px]" />}
                          className="!text-[10px] !py-1 !bg-[#0070C2]"
                          onClick={() => {
                            setBulkSubmit({
                              payerId: claim?.payer_id,
                              month: stats?.month,
                            });
                            setShowConfirmModalBulkSubmission(true);
                          }}
                        />
                        <AppButton
                          label={t("Stop")}
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<CloseIcon className="!text-[12px]" />}
                          className="!text-[10px] !py-1 !border-amber-400 !text-amber-600 hover:!bg-amber-50"
                          onClick={() => {
                            setBulkSubmitStop({
                              payerId: claim?.payer_id,
                              month: stats?.month,
                            });
                            setShowConfirmModalStopBulkSubmission(true);
                          }}
                        />
                        <AppButton
                          label={t("Delete")}
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<MdDelete className="!text-[12px]" />}
                          className="!text-[10px] !py-1 !border-red-400 !text-red-500 hover:!bg-red-50"
                          onClick={() => {
                            setBulkDelete({
                              payerId: claim?.payer_id,
                              month: stats?.month,
                            });
                            setShowConfirmModal(true);
                          }}
                        />
                      </div>
                    </Paper>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConformationModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onDelete={() => deleteBulkClaim()}
        text={t("Delete")}
      />
      <ConformationModal
        open={showConfirmModalBulkSubmission}
        setOpen={setShowConfirmModalBulkSubmission}
        onDelete={() => submitBulkClaim()}
        text={t("Submit Bulk")}
      />
      <ConformationModal
        open={showConfirmModalStopBulkSubmission}
        setOpen={setShowConfirmModalStopBulkSubmission}
        onDelete={() => submitStopBulkClaim()}
        text={t("Stop")}
      />
      <ClaimBulkStatsFilter
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        refetch={refetch}
      />
    </>
  );
}
